import dayjs from "dayjs";

/**
 * Rule 10 — resolve the pay range subscription visible for the selected as-of date.
 * Returns null when the subscription has not started yet or has already ended.
 */
export function resolveSubscriptionForAsOf(subscription, asOfDate) {
  if (!subscription) return null;
  if (!asOfDate) return subscription;

  const asOf = dayjs(asOfDate);
  if (!asOf.isValid()) return subscription;

  if (subscription.effectiveFrom) {
    const from = dayjs(subscription.effectiveFrom);
    if (from.isValid() && asOf.isBefore(from, "day")) return null;
  }

  if (subscription.effectiveTo) {
    const to = dayjs(subscription.effectiveTo);
    if (to.isValid() && asOf.isAfter(to, "day")) return null;
  }

  return subscription;
}

/**
 * ER-11714 — resolve the version the row's subscription card should display for
 * the selected as-of date, from the resolved `versions` chain rather than the
 * flat version_* fields (which are the subscribe-time version, provenance only).
 *
 * - When `versions` is present (ER-11711 payload), pick the version in effect as
 *   of `asOfDate`: the last version whose effectiveFrom is on/before asOfDate and
 *   whose window is still open (effectiveTo null) or ends on/after asOfDate.
 * - When `versions` is absent/empty (older payload, or a single-version
 *   subscription), fall back to the flat fields so the card renders as before.
 */
export function resolveCardVersion(subscription, asOfDate) {
  const versions = Array.isArray(subscription?.versions) ? subscription.versions : [];
  if (versions.length === 0) {
    return {
      versionNumber: subscription?.versionNumber ?? null,
      versionEffectiveFrom: subscription?.versionEffectiveFrom ?? null,
      versionEffectiveTo: subscription?.versionEffectiveTo ?? null,
    };
  }

  const asOf = asOfDate ? dayjs(asOfDate) : dayjs();
  let current = versions[0];

  for (const version of versions) {
    const from = dayjs(version.effectiveFrom);
    const to = version.effectiveTo ? dayjs(version.effectiveTo) : null;

    // Versions arrive oldest-first. A version starting after asOf is in the
    // future, so the previous iteration already found the in-effect version.
    if (from.isValid() && from.isAfter(asOf, "day")) break;

    current = version;

    // A version whose window already ended before asOf is superseded; keep
    // scanning. An open or still-current window (to null or to >= asOf) wins.
    if (to && to.isValid() && !to.isAfter(asOf, "day")) continue;
    break;
  }

  return {
    versionNumber: current?.versionNumber ?? null,
    versionEffectiveFrom: current?.effectiveFrom ?? null,
    versionEffectiveTo: current?.effectiveTo ?? null,
  };
}
